import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useOutletContext, useLocation, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Calendar,
  FolderOpen,
  ArrowRight,
  Layers,
  BarChart3,
  LayoutGrid,
  List,
  Plus,
  Building2,
  Users,
  EyeOff,
  Repeat,
  Kanban,
} from "lucide-react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";

import { Button } from "../../components/Button";
import { Card, CardContent } from "../../components/Card";
import { CustomSelector } from "../../components/CustomSelector";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import SuccessPopup from "../../components/SuccessPopup";
import { EmptyStateContainer } from "../../components/EmptyStateContainer";

import { CustomTextField } from "../../components/CustomTextField";
import { CustomTextArea } from "../../components/CustomTextArea";
import { Label } from "../../components/Label";
import { UnifiedModal } from "../../components/UnifiedModal";
import { ConfirmModal } from "../../components/ConfirmModal";
import apiClient from "../../api-client";
import { isClientReadOnly, withClientAccess } from "@/utils/clientAccess";
import { refreshWelcomeProgress } from "../../Tutorials/welcomeProgress";
import { EmptyStateMessage } from "../../components/EmptyStateMessage";
import { PROJECT_STATE_BADGES } from "./stateBadges";
import { IncidentFiltersModal } from "../../components/IncidentFiltersModal";
import { ProjectCard } from "../../components/ProjectCard";
import { RecurringProjectConfigModal } from "../../components/Projects_components/RecurringProjectConfigModal";
import { InfoTooltip } from "../../components/InfoTooltip";
import { ViewModeSelector } from "../../components/ViewModeSelector";
import { DataTableToolbar, toolbarViewModeButtonSx } from "../../components/DataTable";
import { TemplateEditorModal } from "./ProjectTemplates";
import { CompactClientCard } from "../Clients/CompactClientCard";
import { ProjectsTableView } from "./components/ProjectsTableView";
import { ProjectsKanbanView } from "./components/ProjectsKanbanView";
import { RecurrentProjectsKpiView } from "./components/RecurrentProjectsKpiView";
import { RecurrentProjectsAgencyDetailModal } from "./components/RecurrentProjectsAgencyDetailModal";
import {
  CUSTOM_PROJECTS_TUTORIAL_PARAM,
  CUSTOM_PROJECTS_TUTORIAL_VALUE,
  CUSTOM_PROJECTS_TUTORIAL_PHASE_PARAM,
  CUSTOM_PROJECTS_TUTORIAL_STEP_PARAM,
  CUSTOM_PROJECTS_TUTORIAL_PHASES,
  CUSTOM_PROJECTS_TUTORIAL_PREFERENCE_KEY,
  CUSTOM_PROJECTS_TUTORIAL_INTRO,
  CUSTOM_PROJECTS_TUTORIAL_COMPLETION_DESCRIPTION,
  CUSTOM_PROJECTS_OVERVIEW_STEPS,
  CUSTOM_PROJECTS_CREATE_TRIGGER_STEPS,
  CUSTOM_PROJECTS_PROJECT_MODAL_SETUP_STEPS,
  CUSTOM_PROJECTS_PROJECT_MODAL_TEMPLATE_STEPS,
  CUSTOM_PROJECTS_TUTORIAL_CLIENT,
  CUSTOM_PROJECTS_TUTORIAL_TEMPLATE,
  CUSTOM_PROJECTS_TUTORIAL_PROJECT_FORM,
  CUSTOM_PROJECTS_TUTORIAL_PROJECT,
  getCustomProjectsPhaseSteps,
} from "../../Tutorials/projects/customProjectsTutorial";
import {
  RECURRENT_PROJECTS_TUTORIAL_PREFERENCE_KEY,
  RECURRENT_PROJECTS_TUTORIAL_PARAM,
  RECURRENT_PROJECTS_TUTORIAL_VALUE,
  RECURRENT_PROJECTS_TUTORIAL_PHASE_PARAM,
  RECURRENT_PROJECTS_TUTORIAL_PHASES,
  RECURRENT_PROJECTS_TUTORIAL_INTRO,
  RECURRENT_PROJECTS_TUTORIAL_COMPLETION_DESCRIPTION,
  RECURRENT_PROJECTS_OVERVIEW_STEPS,
  RECURRENT_PROJECTS_MENU_TRIGGER_STEPS,
  RECURRENT_PROJECTS_MENU_STEPS,
  RECURRENT_PROJECTS_CONFIG_MODAL_STEPS,
  RECURRENT_PROJECTS_TUTORIAL_PROJECTS,
  RECURRENT_PROJECTS_TUTORIAL_FEATURED_PROJECT_ID,
  RECURRENT_PROJECTS_TUTORIAL_SUBSCRIPTION,
  RECURRENT_PROJECTS_TUTORIAL_CLIENT,
  RECURRENT_PROJECTS_TUTORIAL_PACK_LABELS,
  getRecurrentProjectsPhaseSteps,
} from "../../Tutorials/projects/recurrentProjectsTutorial";
import {
  useTutorialsEnabled,
  TutorialIntroModal,
  TutorialCompletionModal,
  TutorialJoyrideTooltip,
  getTutorialJoyrideStyles,
  TutorialStartButton,
  TUTORIAL_FLOATER_PROPS,
} from "../../Tutorials/index.js";
import { TutorialTargetProxies } from "../../Tutorials/TutorialTargetProxies.jsx";
import {
  prepareTutorialStep,
  runWhenTutorialTargetReady,
  withTutorialProxyTargets,
} from "../../Tutorials/tutorialTargets.js";
import { FEATURE_RECURRING_PROJECTS, hasAgencyFeature } from "../../utils/agencyFeatures";

const PROJECTS_PAGE_SIZE = 40;
// Modales del tutorial: sin trampa de foco, que si no le roba el foco al tooltip de Joyride.
const TUTORIAL_DIALOG_PROPS = {
  disableEnforceFocus: true,
  disableAutoFocus: true,
  disableRestoreFocus: true,
};
const noopTutorialHandler = () => undefined;
const PROJECTS_TABLE_DEFAULT_PAGE_SIZE = 25;
const VIEW_MODE_OPTIONS = [
  { value: "kanban", label: "Kanban", icon: Kanban },
  { value: "cards", label: "Tarjetas", icon: LayoutGrid },
  { value: "table", label: "Tabla", icon: List },
];
const SORT_DIRECTION_OPTIONS = [
  { value: "asc", label: "Ascendente" },
  { value: "desc", label: "Descendente" },
];
const SORT_FIELD_OPTIONS = [
  { value: "created_at", label: "Fecha de creación" },
  { value: "start_date", label: "Fecha de inicio" },
];
const PROJECT_TYPE_CONFIG = {
  custom: {
    routeSegment: "custom",
    apiValue: "custom",
    singularLabel: "personalizado",
    pluralLabel: "personalizados",
  },
  recurrent: {
    routeSegment: "recurrent",
    apiValue: "recurrent_monthly",
    singularLabel: "recurrente",
    pluralLabel: "recurrentes",
  },
};

const formatDate = (value) => {
  if (!value) return "Sin fecha";
  try {
    return new Date(value).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

const extractErrorMessage = (error) => {
  if (error?.response?.data) {
    const data = error.response.data;

    if (typeof data === "string") {
      return data;
    }

    if (Array.isArray(data)) {
      return data.join(", ");
    }

    if (data.detail) {
      return data.detail;
    }

    const [firstKey] = Object.keys(data);
    if (firstKey) {
      const value = data[firstKey];
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      if (typeof value === "string") {
        return value;
      }
    }
  }

  return "Error al procesar la solicitud. Intenta nuevamente.";
};

const createEmptyAdvancedFilters = () => ({
  agency: null,
  client: null,
});

const cloneAdvancedFilters = (filters) => ({
  agency: filters.agency ? { ...filters.agency } : null,
  client: filters.client ? { ...filters.client } : null,
});

// Fase de tutorial indicada en la URL. Solo se aceptan fases que arrancan sin modales abiertos;
// las de plantillas viven en ProjectTemplates.
const resolveTutorialPhaseFromParams = (projectType, searchParams) => {
  if (
    projectType === "custom" &&
    searchParams.get(CUSTOM_PROJECTS_TUTORIAL_PARAM) === CUSTOM_PROJECTS_TUTORIAL_VALUE
  ) {
    const phaseParam = searchParams.get(CUSTOM_PROJECTS_TUTORIAL_PHASE_PARAM);
    return phaseParam === CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_CREATE_TRIGGER
      ? phaseParam
      : CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECTS_OVERVIEW;
  }
  if (
    projectType === "recurrent" &&
    searchParams.get(RECURRENT_PROJECTS_TUTORIAL_PARAM) === RECURRENT_PROJECTS_TUTORIAL_VALUE
  ) {
    return RECURRENT_PROJECTS_TUTORIAL_PHASES.OVERVIEW;
  }
  return null;
};

const normalizeApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const ProjectsGridSkeleton = () => (
  <Grid container spacing={3}>
    {Array.from({ length: 3 }).map((_, index) => (
      <Grid size={{ xs: 12, md: 6, xl: 4 }} key={`project-skeleton-${index}`}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Box>
                <Skeleton variant="text" width="65%" height={28} />
                <Skeleton variant="text" width="45%" height={18} />
              </Box>
              <Skeleton variant="rounded" height={8} />
              <Skeleton variant="text" width="55%" height={18} />
              <Skeleton variant="rounded" height={36} />
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

function CreateProjectModal({
  open,
  onClose,
  clients,
  templates,
  defaultClientId,
  onSubmit,
  isSubmitting,
  onClientChange,
  templatesDisabled,
  templatesHelperText,
  tutorialInitialState = null,
  disableBackdropClose = false,
  dialogProps = null,
}) {
  const today = useMemo(() => new Date().toISOString().split("T")[0], []);
  const templateUsageOptions = useMemo(
    () => [
      { value: "yes", label: "Sí" },
      { value: "no", label: "No" },
    ],
    []
  );
  const [formState, setFormState] = useState({
    client: "",
    useTemplate: "yes",
    template: "",
    isVisibleToClient: true,
    startDate: today,
    name: "",
    description: "",
  });
  const [localError, setLocalError] = useState(null);
  const [definitionModalOpen, setDefinitionModalOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (tutorialInitialState) {
      setFormState({
        client: tutorialInitialState.client || "",
        useTemplate: tutorialInitialState.useTemplate || "yes",
        template: tutorialInitialState.template || "",
        isVisibleToClient: tutorialInitialState.isVisibleToClient ?? true,
        startDate: tutorialInitialState.startDate || today,
        name: tutorialInitialState.name || "",
        description: tutorialInitialState.description || "",
      });
    } else {
      setFormState((prev) => ({
        ...prev,
        client: defaultClientId || prev.client || "",
        useTemplate: prev.useTemplate || "yes",
        startDate: prev.startDate || today,
      }));
    }
    setLocalError(null);
    setDefinitionModalOpen(false);
  }, [open, defaultClientId, today, tutorialInitialState]);

  const usesTemplate = formState.useTemplate === "yes";
  const isValid =
    formState.client && formState.startDate && (usesTemplate ? formState.template : true);

  const handleSubmitWithTemplate = () => {
    if (!isValid || isSubmitting) {
      setLocalError("Completa los campos obligatorios para crear el proyecto.");
      return;
    }

    setLocalError(null);
    onSubmit({
      client: formState.client,
      template: formState.template,
      startDate: formState.startDate,
      name: formState.name.trim(),
      description: formState.description.trim(),
      is_internal: !formState.isVisibleToClient,
    });
  };

  const handleOpenProjectDefinition = () => {
    if (!isValid || isSubmitting) {
      setLocalError("Completa los campos obligatorios para definir el proyecto.");
      return;
    }

    setLocalError(null);
    setDefinitionModalOpen(true);
  };

  const handlePrimaryAction = () => {
    if (usesTemplate) {
      handleSubmitWithTemplate();
      return;
    }
    handleOpenProjectDefinition();
  };

  return (
    <>
      <UnifiedModal
        open={open}
        onClose={onClose}
        title="Nuevo proyecto"
        disableBackdropClose={disableBackdropClose}
        {...dialogProps}
      >
        <Stack spacing={3}>
          <Box data-tutorial="create-project-primary-fields">
            <Box>
              <Label>Cliente</Label>
              <Box sx={{ mt: 1 }}>
                <CustomSelector
                  value={formState.client}
                  onChange={(value) => {
                    setFormState((prev) => ({
                      ...prev,
                      client: value,
                      template:
                        prev.useTemplate === "yes" && value === prev.client
                          ? prev.template
                          : "",
                    }));
                    if (onClientChange) {
                      onClientChange(value);
                    }
                  }}
                  options={clients}
                  placeholder="Selecciona un cliente"
                  searchable
                />
              </Box>
            </Box>

            <Grid container spacing={3} sx={{ mt: 3 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <InfoTooltip
                  title="Seleccionar una plantilla permite crear un proyecto que automáticamente utilice las fases y tareas definidas en la plantilla"
                  zIndex={100001}
                >
                  <Label>¿Desea usar una plantilla?</Label>
                </InfoTooltip>
                <Box sx={{ mt: 1 }}>
                  <CustomSelector
                    value={formState.useTemplate}
                    onChange={(value) =>
                      setFormState((prev) => ({
                        ...prev,
                        useTemplate: value,
                        template: value === "yes" ? prev.template : "",
                      }))
                    }
                    options={templateUsageOptions}
                    placeholder="Selecciona una opción"
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <InfoTooltip
                  title="Ocultar el proyecto permite utilizarlo internamente"
                  zIndex={100001}
                >
                  <Label>¿Proyecto visible al cliente?</Label>
                </InfoTooltip>
                <Box sx={{ mt: 1 }}>
                  <CustomSelector
                    value={formState.isVisibleToClient ? "yes" : "no"}
                    onChange={(value) =>
                      setFormState((prev) => ({
                        ...prev,
                        isVisibleToClient: value === "yes",
                      }))
                    }
                    options={templateUsageOptions}
                    placeholder="Selecciona una opción"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {usesTemplate && (
            <Box data-tutorial="create-project-template-selector">
              <Label>Plantilla</Label>
              <Box sx={{ mt: 1 }}>
                <CustomSelector
                  value={formState.template}
                  onChange={(value) =>
                    setFormState((prev) => ({ ...prev, template: value }))
                  }
                  options={templates}
                  placeholder="Selecciona una plantilla"
                  disabled={templatesDisabled}
                  searchable
                />
              </Box>
              {templatesDisabled && templatesHelperText ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {templatesHelperText}
                </Typography>
              ) : null}
            </Box>
          )}

          <Box data-tutorial="create-project-details">
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Label>Nombre del proyecto</Label>
                <Box sx={{ mt: 1 }}>
                  <CustomTextField
                    value={formState.name}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        name: event.target.value,
                      }))
                    }
                    placeholder={
                      usesTemplate
                        ? "Opcional — sobrescribe el nombre de la plantilla"
                        : "Nombre del proyecto"
                    }
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Label>Fecha de inicio</Label>
                <Box sx={{ mt: 1 }}>
                  <CustomTextField
                    type="date"
                    value={formState.startDate}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        startDate: event.target.value,
                      }))
                    }
                  />
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Label>Descripción</Label>
              <Box sx={{ mt: 1 }}>
                <CustomTextArea
                  value={formState.description}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      description: event.target.value,
                    }))
                  }
                  placeholder="Opcional — añade detalles para el equipo"
                  className="h-28"
                />
              </Box>
            </Box>
          </Box>

          {localError && (
            <Typography variant="body2" sx={{ color: "error.main" }}>
              {localError}
            </Typography>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button
              data-tutorial="create-project-submit"
              onClick={handlePrimaryAction}
              disabled={!isValid || isSubmitting}
            >
              <Plus size={16} style={{ marginRight: "8px" }} />
              {usesTemplate ? "Crear proyecto" : "Definir proyecto"}
            </Button>
          </Stack>
        </Stack>
      </UnifiedModal>

      <TemplateEditorModal
        open={definitionModalOpen}
        onClose={() => setDefinitionModalOpen(false)}
        initialData={null}
        mode="create"
        onSaved={() => { }}
        canEdit={true}
        agencyId={null}
        isSuperAdmin={false}
        titleOverride="Crear proyecto"
        submitLabelOverride="Crear proyecto"
        initialName={formState.name}
        initialDescription={formState.description}
        onSubmitOverride={async (projectDefinition) => {
          setFormState((prev) => ({
            ...prev,
            name: projectDefinition.name || "",
            description: projectDefinition.description || "",
          }));

          await onSubmit({
            client: formState.client,
            template: null,
            startDate: formState.startDate,
            name: (projectDefinition.name || "").trim(),
            description: (projectDefinition.description || "").trim(),
            phases: projectDefinition.phases || [],
            is_internal: !formState.isVisibleToClient,
          });
        }}
      />
    </>
  );
}

export function Projects({ projectType = "custom" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser, interceptorReady } = useAuth();
  const theme = useTheme();
  const tutorialsEnabled = useTutorialsEnabled();
  const projectTypeConfig =
    PROJECT_TYPE_CONFIG[projectType] || PROJECT_TYPE_CONFIG.custom;
  const projectTypeApiValue = projectTypeConfig.apiValue;
  const loadMoreRef = useRef(null);
  const {
    setPageContext,
    selectedFilter,
    selectedFilterData,
    changeSelectedFilter,
    filterRefresh,
    projectsRefreshKey,
    clientIds,
    isMultiClientUser,
    updateUiPreferences,
  } = useOutletContext();

  const [selectedClient, setSelectedClient] = useState("__all__");
  const [createProjectClientId, setCreateProjectClientId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("__all__");
  const [statusStats, setStatusStats] = useState({
    in_progress: 0,
    not_started: 0,
    completed: 0,
    all: 0,
  });
  const [viewMode, setViewMode] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc");
  const [sortField, setSortField] = useState("created_at");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const searchDebounceTimerRef = useRef(null);
  const lastSearchRef = useRef("");
  const [searchRefreshKey, setSearchRefreshKey] = useState(0);
  const [sortRefreshKey, setSortRefreshKey] = useState(0);
  const previousFilterRef = useRef(selectedFilter);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [shouldLoadTemplates, setShouldLoadTemplates] = useState(false);
  const [templatesRefreshKey, setTemplatesRefreshKey] = useState(0);
  const [shouldLoadClients, setShouldLoadClients] = useState(false);
  const [clientsRefreshKey, setClientsRefreshKey] = useState(0);
  const [createProjectRefreshKey, setCreateProjectRefreshKey] = useState(0);
  const [filterModalRefreshKey, setFilterModalRefreshKey] = useState(0);
  const [advancedFiltersRefreshKey, setAdvancedFiltersRefreshKey] = useState(0);
  const [localProjectsRefreshKey, setLocalProjectsRefreshKey] = useState(0);
  const [localProjectsOverrides, setLocalProjectsOverrides] = useState({});
  const [projectsPage, setProjectsPage] = useState(1);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(PROJECTS_TABLE_DEFAULT_PAGE_SIZE);
  const [projectsList, setProjectsList] = useState([]);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const [totalProjects, setTotalProjects] = useState(null);
  const shouldShowClientColumn =
    currentUser?.role !== "client" || (isMultiClientUser && !selectedFilter);

  // Estados para filtros avanzados
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState(() =>
    createEmptyAdvancedFilters()
  );
  const [draftAdvancedFilters, setDraftAdvancedFilters] = useState(() =>
    createEmptyAdvancedFilters()
  );

  // Estado para confirmación de eliminación de proyecto
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteProjectId, setDeleteProjectId] = useState(null);

  // Estado para confirmación de ocultar/mostrar proyecto
  const [visibilityConfirmOpen, setVisibilityConfirmOpen] = useState(false);
  const [visibilityProjectId, setVisibilityProjectId] = useState(null);
  const [visibilityCurrentState, setVisibilityCurrentState] = useState(false);
  const [stateConfirmOpen, setStateConfirmOpen] = useState(false);
  const [pendingStateChange, setPendingStateChange] = useState(null);
  const [recurringConfigModalOpen, setRecurringConfigModalOpen] = useState(
    false
  );
  const [selectedRecurringProject, setSelectedRecurringProject] = useState(
    null
  );
  const [recurringConfigRefreshKey, setRecurringConfigRefreshKey] =
    useState(0);
  const [selectedKpiAgency, setSelectedKpiAgency] = useState(null);
  const [isKpiAgencyDetailOpen, setIsKpiAgencyDetailOpen] = useState(false);

  // ── TUTORIALES DE PROYECTOS ───────────────────────────────────────────────
  const isCustomProjectsPage = projectType === "custom";
  const isRecurrentProjectsPage = projectType === "recurrent";
  const isSuperadminRecurrentKpiAvailable =
    currentUser?.role === "superadmin" && isRecurrentProjectsPage;
  const canStartProjectsTutorial =
    tutorialsEnabled &&
    (isCustomProjectsPage || isRecurrentProjectsPage) &&
    ["agency_admin", "consultant"].includes(currentUser?.role);
  const currentTutorialPreferenceKey = isCustomProjectsPage
    ? CUSTOM_PROJECTS_TUTORIAL_PREFERENCE_KEY
    : RECURRENT_PROJECTS_TUTORIAL_PREFERENCE_KEY;
  const currentTutorialIntro = isCustomProjectsPage
    ? CUSTOM_PROJECTS_TUTORIAL_INTRO
    : RECURRENT_PROJECTS_TUTORIAL_INTRO;
  const currentTutorialCompletionDescription = isCustomProjectsPage
    ? CUSTOM_PROJECTS_TUTORIAL_COMPLETION_DESCRIPTION
    : RECURRENT_PROJECTS_TUTORIAL_COMPLETION_DESCRIPTION;
  // Tras el tutorial de personalizados se ofrece el de recurrentes solo si la agencia lo tiene.
  const canOfferRecurrentTutorial =
    isCustomProjectsPage && hasAgencyFeature(currentUser, FEATURE_RECURRING_PROJECTS);

  // Init sincrónico desde URL params (vuelta desde plantillas o salto desde el tutorial de personalizados).
  const [tutorialPhase, setTutorialPhase] = useState(() =>
    resolveTutorialPhaseFromParams(projectType, searchParams)
  );
  const [tutorialJoyrideKey, setTutorialJoyrideKey] = useState(0);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [isTutorialJoyrideRunning, setIsTutorialJoyrideRunning] = useState(false);
  const [showTutorialIntroModal, setShowTutorialIntroModal] = useState(false);
  const [isFirstTimeTutorialPrompt, setIsFirstTimeTutorialPrompt] = useState(false);
  const [showTutorialCompletionModal, setShowTutorialCompletionModal] = useState(false);
  const [tutorialCreateModalOpen, setTutorialCreateModalOpen] = useState(false);
  const [tutorialRecurringConfigModalOpen, setTutorialRecurringConfigModalOpen] =
    useState(false);
  const [tutorialClientPreviewOpen, setTutorialClientPreviewOpen] =
    useState(false);
  const tutorialAutoPromptedRef = useRef({});
  const tutorialReturnHandledRef = useRef(false);

  const tutorialActive = tutorialsEnabled && tutorialPhase !== null;
  const viewModeOptions = useMemo(() => {
    if (!isSuperadminRecurrentKpiAvailable) {
      return VIEW_MODE_OPTIONS;
    }

    return [
      { value: "kpis", label: "KPIs", icon: BarChart3 },
      ...VIEW_MODE_OPTIONS,
    ];
  }, [isSuperadminRecurrentKpiAvailable]);

  // Superadmin en recurrentes aterriza en los KPIs; el resto en el Kanban.
  const defaultViewMode = isSuperadminRecurrentKpiAvailable ? "kpis" : "kanban";
  const resolvedViewMode = viewMode || defaultViewMode;
  // Los tutoriales se hacen sobre la vista de tabla, sea cual sea la vista elegida.
  const effectiveViewMode = tutorialActive ? "table" : resolvedViewMode;
  const isKpiView = effectiveViewMode === "kpis";

  const projectsOrdering = useMemo(
    () => (sortDirection === "desc" ? `-${sortField}` : sortField),
    [sortDirection, sortField]
  );
  const isTableView = effectiveViewMode === "table";
  const currentProjectsPageSize = isTableView ? tableRowsPerPage : PROJECTS_PAGE_SIZE;

  const tutorialMockProjects = useMemo(() => {
    if (isCustomProjectsPage) {
      return tutorialPhase === CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_CARD
        ? [CUSTOM_PROJECTS_TUTORIAL_PROJECT]
        : [];
    }

    if (isRecurrentProjectsPage) {
      return [
        RECURRENT_PROJECTS_TUTORIAL_PHASES.OVERVIEW,
        RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU_TRIGGER,
        RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU,
        RECURRENT_PROJECTS_TUTORIAL_PHASES.CONFIG_MODAL,
        RECURRENT_PROJECTS_TUTORIAL_PHASES.CLIENT_CARD,
      ].includes(tutorialPhase)
        ? RECURRENT_PROJECTS_TUTORIAL_PROJECTS
        : [];
    }

    return [];
  }, [isCustomProjectsPage, isRecurrentProjectsPage, tutorialPhase]);

  const TUTORIAL_CLIENT_OPTIONS = useMemo(() => [CUSTOM_PROJECTS_TUTORIAL_CLIENT], []);
  const TUTORIAL_TEMPLATE_OPTIONS = useMemo(() => [CUSTOM_PROJECTS_TUTORIAL_TEMPLATE], []);

  const isModalTutorialPhase = isCustomProjectsPage
    ? [
      CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_MODAL_SETUP,
      CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_MODAL_TEMPLATE,
    ].includes(tutorialPhase)
    : [
      RECURRENT_PROJECTS_TUTORIAL_PHASES.CONFIG_MODAL,
      RECURRENT_PROJECTS_TUTORIAL_PHASES.CLIENT_CARD,
    ].includes(tutorialPhase);
  const currentTutorialJoyrideZIndex = isModalTutorialPhase ? 200000 : 10000;

  const tutorialPrefState =
    currentUser?.ui_preferences?.tutorials?.[currentTutorialPreferenceKey] || {};

  const markTutorialVisited = useCallback(async () => {
    if (tutorialPrefState.visited_at) return;
    await updateUiPreferences({
      tutorials: {
        [currentTutorialPreferenceKey]: { visited_at: new Date().toISOString() },
      },
    });
  }, [currentTutorialPreferenceKey, tutorialPrefState.visited_at, updateUiPreferences]);

  useEffect(() => {
    tutorialReturnHandledRef.current = false;
    setIsTutorialJoyrideRunning(false);
    setTutorialCreateModalOpen(false);
    setTutorialRecurringConfigModalOpen(false);
    setTutorialClientPreviewOpen(false);
    setShowTutorialIntroModal(false);
    setShowTutorialCompletionModal(false);
    setTutorialPhase((prev) => {
      if (!prev) return null;
      const validPhases = isCustomProjectsPage
        ? new Set(Object.values(CUSTOM_PROJECTS_TUTORIAL_PHASES))
        : new Set(Object.values(RECURRENT_PROJECTS_TUTORIAL_PHASES));
      return validPhases.has(prev) ? prev : null;
    });
  }, [isCustomProjectsPage, isRecurrentProjectsPage]);

  // Auto-prompt primera vez según la sección actual.
  useEffect(() => {
    const tutorialPageKey = isCustomProjectsPage ? "custom" : "recurrent";
    if (
      !canStartProjectsTutorial ||
      !currentUser?.id ||
      tutorialActive ||
      tutorialAutoPromptedRef.current[tutorialPageKey] ||
      tutorialPrefState.visited_at
    ) return;
    tutorialAutoPromptedRef.current[tutorialPageKey] = true;
    setIsFirstTimeTutorialPrompt(true);
    setShowTutorialIntroModal(true);
  }, [
    canStartProjectsTutorial,
    currentUser?.id,
    isCustomProjectsPage,
    tutorialActive,
    tutorialPrefState.visited_at,
  ]);

  useEffect(() => {
    if (!tutorialActive || tutorialPrefState.visited_at) return;

    markTutorialVisited().catch((error) => {
      console.error("Error guardando preferencia de tutorial:", error);
    });
  }, [tutorialActive, tutorialPrefState.visited_at, markTutorialVisited]);

  const cleanTutorialUrlParams = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(CUSTOM_PROJECTS_TUTORIAL_PARAM);
      next.delete(CUSTOM_PROJECTS_TUTORIAL_PHASE_PARAM);
      next.delete(CUSTOM_PROJECTS_TUTORIAL_STEP_PARAM);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Detectar entrada al tutorial por URL (vuelta desde plantillas o salto desde el de personalizados).
  // Se fija también la fase por si React reutiliza la instancia al cambiar de custom a recurrent.
  useEffect(() => {
    if (tutorialReturnHandledRef.current) return;

    const phaseFromParams = resolveTutorialPhaseFromParams(projectType, searchParams);
    if (!phaseFromParams) return;
    tutorialReturnHandledRef.current = true;
    setTutorialPhase(phaseFromParams);

    if (isCustomProjectsPage) {
      const stepParam = searchParams.get(CUSTOM_PROJECTS_TUTORIAL_STEP_PARAM);
      setTutorialStepIndex(stepParam !== null ? parseInt(stepParam, 10) || 0 : 0);
    } else {
      setTutorialStepIndex(0);
      cleanTutorialUrlParams();
    }
    setTutorialJoyrideKey((k) => k + 1);
    setTutorialRunRequest((n) => n + 1);
  }, [projectType, isCustomProjectsPage, searchParams, cleanTutorialUrlParams]);

  const handleCloseTutorialIntro = useCallback(async () => {
    setShowTutorialIntroModal(false);
    setIsFirstTimeTutorialPrompt(false);
    try {
      await markTutorialVisited();
    } catch (err) {
      console.error("Error guardando preferencia de tutorial:", err);
    }
  }, [markTutorialVisited]);

  const startProjectsTutorial = useCallback(() => {
    setShowTutorialIntroModal(false);
    setIsFirstTimeTutorialPrompt(false);
    setTutorialPhase(
      isCustomProjectsPage
        ? CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECTS_OVERVIEW
        : RECURRENT_PROJECTS_TUTORIAL_PHASES.OVERVIEW
    );
    // Al completar un recorrido el índice queda en el último paso + 1.
    setTutorialStepIndex(0);
    markTutorialVisited().catch(console.error).finally(() => {
      setTutorialJoyrideKey((k) => k + 1);
      setTutorialRunRequest((n) => n + 1);
    });
  }, [isCustomProjectsPage, markTutorialVisited]);

  const resetTutorial = useCallback(() => {
    setTutorialPhase(null);
    setIsTutorialJoyrideRunning(false);
    setTutorialStepIndex(0);
    setTutorialCreateModalOpen(false);
    setTutorialRecurringConfigModalOpen(false);
    setTutorialClientPreviewOpen(false);
    cleanTutorialUrlParams();
  }, [cleanTutorialUrlParams]);

  // Joyride arranca cuando el target del paso actual está montado y quieto (ver efecto más abajo).
  const [tutorialRunRequest, setTutorialRunRequest] = useState(0);
  const requestTutorialRun = useCallback(() => setTutorialRunRequest((n) => n + 1), []);

  const advanceTutorial = useCallback((nextPhase, { closeModal, openModal, startStepIndex = 0 } = {}) => {
    setIsTutorialJoyrideRunning(false);
    if (closeModal) closeModal();
    setTutorialPhase(nextPhase);
    setTutorialStepIndex(startStepIndex);
    setTutorialJoyrideKey((k) => k + 1);
    if (openModal) openModal();
    requestTutorialRun();
  }, [requestTutorialRun]);

  const firstStepOnBackClick = useMemo(() => {
    if (isCustomProjectsPage) {
      switch (tutorialPhase) {
        case CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECTS_OVERVIEW:
          return undefined;
        case CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_CREATE_TRIGGER:
          return currentUser?.role === "consultant"
            ? () => advanceTutorial(CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECTS_OVERVIEW, {
                startStepIndex: CUSTOM_PROJECTS_OVERVIEW_STEPS.length - 1,
              })
            : () => navigate(
                `/projects/templates?from=custom&${CUSTOM_PROJECTS_TUTORIAL_PARAM}=${CUSTOM_PROJECTS_TUTORIAL_VALUE}&${CUSTOM_PROJECTS_TUTORIAL_PHASE_PARAM}=${CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_CREATED}`
              );
        case CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_MODAL_SETUP:
          return () => advanceTutorial(CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_CREATE_TRIGGER, {
            closeModal: () => setTutorialCreateModalOpen(false),
            startStepIndex: CUSTOM_PROJECTS_CREATE_TRIGGER_STEPS.length - 1,
          });
        case CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_MODAL_TEMPLATE:
          return () => advanceTutorial(CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_MODAL_SETUP, {
            startStepIndex: CUSTOM_PROJECTS_PROJECT_MODAL_SETUP_STEPS.length - 1,
          });
        case CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_CARD:
          return () => advanceTutorial(CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_MODAL_TEMPLATE, {
            openModal: () => setTutorialCreateModalOpen(true),
            startStepIndex: CUSTOM_PROJECTS_PROJECT_MODAL_TEMPLATE_STEPS.length - 1,
          });
        default:
          return undefined;
      }
    }

    switch (tutorialPhase) {
      case RECURRENT_PROJECTS_TUTORIAL_PHASES.OVERVIEW:
        return undefined;
      case RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU_TRIGGER:
        return () => advanceTutorial(RECURRENT_PROJECTS_TUTORIAL_PHASES.OVERVIEW, {
          startStepIndex: RECURRENT_PROJECTS_OVERVIEW_STEPS.length - 1,
        });
      case RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU:
        return () => advanceTutorial(RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU_TRIGGER, {
          startStepIndex: RECURRENT_PROJECTS_MENU_TRIGGER_STEPS.length - 1,
        });
      case RECURRENT_PROJECTS_TUTORIAL_PHASES.CONFIG_MODAL:
        return () => advanceTutorial(RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU, {
          closeModal: () => setTutorialRecurringConfigModalOpen(false),
          startStepIndex: RECURRENT_PROJECTS_MENU_STEPS.length - 1,
        });
      case RECURRENT_PROJECTS_TUTORIAL_PHASES.CLIENT_CARD:
        return () => advanceTutorial(RECURRENT_PROJECTS_TUTORIAL_PHASES.CONFIG_MODAL, {
          closeModal: () => setTutorialClientPreviewOpen(false),
          openModal: () => setTutorialRecurringConfigModalOpen(true),
          startStepIndex: RECURRENT_PROJECTS_CONFIG_MODAL_STEPS.length - 1,
        });
      default:
        return undefined;
    }
  }, [
    isCustomProjectsPage,
    tutorialPhase,
    currentUser?.role,
    advanceTutorial,
    navigate,
  ]);

  const currentTutorialJoyrideSteps = useMemo(() => {
    const steps = isCustomProjectsPage
      ? getCustomProjectsPhaseSteps(tutorialPhase, currentUser?.role)
      : getRecurrentProjectsPhaseSteps(tutorialPhase);

    return withTutorialProxyTargets(steps, `${projectType}-projects`).map((step, i) => ({
      ...step,
      ...(i === 0 && firstStepOnBackClick ? { onBackClick: firstStepOnBackClick } : {}),
    }));
  }, [isCustomProjectsPage, projectType, tutorialPhase, firstStepOnBackClick, currentUser?.role]);

  useEffect(() => {
    if (!tutorialRunRequest || !tutorialActive) return undefined;
    const step = currentTutorialJoyrideSteps[tutorialStepIndex];
    if (!step) return undefined;
    return runWhenTutorialTargetReady(
      step.tutorialSourceTarget,
      () => setIsTutorialJoyrideRunning(true),
      { onTimeout: resetTutorial }
    );
    // Solo al pedir un arranque (fase, paso y pasos ya están al día en ese render) o al salir
    // del tutorial, que cancela la espera pendiente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialRunRequest, tutorialActive]);

  const handleTutorialJoyrideCallback = useCallback(
    ({ action, index, status, type }) => {
      if (status === STATUS.FINISHED) {
        setIsTutorialJoyrideRunning(false);

        if (isCustomProjectsPage) {
          switch (tutorialPhase) {
            case CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECTS_OVERVIEW:
              if (currentUser?.role === "consultant") {
                tutorialReturnHandledRef.current = true;
                advanceTutorial(CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_CREATE_TRIGGER);
              } else {
                navigate(
                  `/projects/templates?from=custom&${CUSTOM_PROJECTS_TUTORIAL_PARAM}=${CUSTOM_PROJECTS_TUTORIAL_VALUE}&${CUSTOM_PROJECTS_TUTORIAL_PHASE_PARAM}=${CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATES_OVERVIEW}`
                );
              }
              break;
            case CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_CREATE_TRIGGER:
              advanceTutorial(CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_MODAL_SETUP, {
                openModal: () => setTutorialCreateModalOpen(true),
              });
              break;
            case CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_MODAL_SETUP:
              advanceTutorial(CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_MODAL_TEMPLATE);
              break;
            case CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_MODAL_TEMPLATE:
              setTutorialCreateModalOpen(false);
              advanceTutorial(CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_CARD);
              break;
            case CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_CARD:
              setTutorialPhase(null);
              cleanTutorialUrlParams();
              setShowTutorialCompletionModal(true);
              break;
            default:
              break;
          }
        } else {
          switch (tutorialPhase) {
            case RECURRENT_PROJECTS_TUTORIAL_PHASES.OVERVIEW:
              advanceTutorial(RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU_TRIGGER);
              break;
            case RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU_TRIGGER:
              advanceTutorial(RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU);
              break;
            case RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU:
              advanceTutorial(RECURRENT_PROJECTS_TUTORIAL_PHASES.CONFIG_MODAL, {
                openModal: () => setTutorialRecurringConfigModalOpen(true),
              });
              break;
            case RECURRENT_PROJECTS_TUTORIAL_PHASES.CONFIG_MODAL:
              setTutorialRecurringConfigModalOpen(false);
              advanceTutorial(RECURRENT_PROJECTS_TUTORIAL_PHASES.CLIENT_CARD, {
                openModal: () => setTutorialClientPreviewOpen(true),
              });
              break;
            case RECURRENT_PROJECTS_TUTORIAL_PHASES.CLIENT_CARD:
              setTutorialClientPreviewOpen(false);
              setTutorialPhase(null);
              setShowTutorialCompletionModal(true);
              break;
            default:
              break;
          }
        }
      }

      if (status === STATUS.SKIPPED) {
        resetTutorial();
        return;
      }

      // Target desmontado (p. ej. un modal aún cerrándose): se espera a que vuelva a estar.
      if (type === EVENTS.TARGET_NOT_FOUND) {
        setIsTutorialJoyrideRunning(false);
        requestTutorialRun();
        return;
      }

      if (type === EVENTS.STEP_AFTER) {
        const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
        prepareTutorialStep(currentTutorialJoyrideSteps, nextIndex);
        setTutorialStepIndex(nextIndex);
      }
    },
    [
      tutorialPhase,
      resetTutorial,
      isCustomProjectsPage,
      currentUser?.role,
      advanceTutorial,
      navigate,
      cleanTutorialUrlParams,
      currentTutorialJoyrideSteps,
      requestTutorialRun,
    ]
  );
  // ── FIN TUTORIALES ────────────────────────────────────────────────────────
  // Debounce para el buscador inline
  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();

      // CASO 1: transición búsqueda → vacío (forzar reset)
      if (trimmed === '' && lastSearchRef.current !== '') {
        lastSearchRef.current = '';
        setSearchTerm('');
        setProjectsPage(1);
        setProjectsList([]);
        setHasMoreProjects(true);
        setSearchRefreshKey(prev => prev + 1);
        return;
      }

      // CASO 2: búsqueda normal (mínimo 2 caracteres)
      if (trimmed.length >= 2 && trimmed !== lastSearchRef.current) {
        lastSearchRef.current = trimmed;
        setSearchTerm(trimmed);
      }
    }, 300);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInput]);

  // Función para abrir modal de confirmación de eliminación
  const handleDeleteProjectClick = useCallback((projectId) => {
    setDeleteProjectId(projectId);
    setDeleteConfirmOpen(true);
  }, []);

  // Función para confirmar eliminación
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteProjectId) return;

    // Cerrar modal inmediatamente
    setDeleteConfirmOpen(false);
    const projectIdToDelete = deleteProjectId;
    setDeleteProjectId(null);

    try {
      await apiClient.delete(`projects/projects/${projectIdToDelete}/`);

      setLocalProjectsRefreshKey((prev) => prev + 1);

      setPopupMessage("Proyecto eliminado correctamente");
      setIsError(false);
      setPopupOpen(true);
    } catch (error) {
      console.error("Error al eliminar proyecto:", error);
      setPopupMessage(extractErrorMessage(error));
      setIsError(true);
      setPopupOpen(true);
    }
  }, [deleteProjectId]);

  // Función para abrir modal de confirmación de visibilidad
  const handleToggleVisibilityClick = useCallback((projectId, currentIsInternal) => {
    setVisibilityProjectId(projectId);
    setVisibilityCurrentState(currentIsInternal);
    setVisibilityConfirmOpen(true);
  }, []);

  // Función para confirmar cambio de visibilidad
  const handleConfirmToggleVisibility = useCallback(async () => {
    if (visibilityProjectId === null) return;

    // Cerrar modal inmediatamente
    setVisibilityConfirmOpen(false);
    const projectId = visibilityProjectId;
    const newIsInternal = !visibilityCurrentState;
    setVisibilityProjectId(null);
    setVisibilityCurrentState(false);

    try {
      await apiClient.patch(`projects/projects/${projectId}/`, {
        is_internal: newIsInternal,
      });

      // Actualizar localmente
      setLocalProjectsOverrides((prev) => ({
        ...prev,
        [projectId]: { is_internal: newIsInternal }
      }));

      setPopupMessage(
        newIsInternal
          ? "Proyecto ocultado al cliente. El thread y sus mensajes han sido eliminados."
          : "Proyecto visible al cliente. Se ha creado un nuevo thread."
      );
      setIsError(false);
      setPopupOpen(true);
    } catch (error) {
      console.error("Error al cambiar visibilidad del proyecto:", error);
      setPopupMessage(extractErrorMessage(error));
      setIsError(true);
      setPopupOpen(true);
    }
  }, [visibilityProjectId, visibilityCurrentState]);
  const handleProjectStateChange = useCallback(async (projectId, newState, previousState) => {
    // previousState is passed from ProjectCard component
    try {
      await apiClient.patch(`projects/projects/${projectId}/`, { state: newState });

      // Actualizar localmente en vez de refrescar
      setLocalProjectsOverrides((prev) => ({
        ...prev,
        [projectId]: { state: newState }
      }));

      // Actualizar statusStats localmente
      if (previousState && previousState !== newState) {
        setStatusStats((prev) => ({
          ...prev,
          [previousState]: Math.max(0, (prev[previousState] || 0) - 1),
          [newState]: (prev[newState] || 0) + 1,
        }));
      }

      setPopupMessage(`Proyecto marcado como ${PROJECT_STATE_BADGES[newState].label}`);
      setIsError(false);
      setPopupOpen(true);
    } catch (error) {
      console.error("Error al cambiar estado del proyecto:", error);
      setPopupMessage(extractErrorMessage(error));
      setIsError(true);
      setPopupOpen(true);
    }
  }, []);

  const requestProjectStateChange = useCallback(
    (projectId, newState, previousState) => {
      if (newState === "completed") {
        setPendingStateChange({ projectId, newState, previousState });
        setStateConfirmOpen(true);
        return;
      }

      handleProjectStateChange(projectId, newState, previousState);
    },
    [handleProjectStateChange]
  );

  const handleConfirmProjectStateChange = useCallback(async () => {
    if (!pendingStateChange) return;

    const { projectId, newState, previousState } = pendingStateChange;
    setStateConfirmOpen(false);
    setPendingStateChange(null);
    await handleProjectStateChange(projectId, newState, previousState);
  }, [pendingStateChange, handleProjectStateChange]);

  const handleOpenRecurringConfig = useCallback((project) => {
    setSelectedRecurringProject(project);
    setRecurringConfigRefreshKey((prev) => prev + 1);
    setRecurringConfigModalOpen(true);
  }, []);

  const handleCloseRecurringConfig = useCallback(() => {
    setRecurringConfigModalOpen(false);
  }, []);

  const handleRecurringConfigSaved = useCallback((subscription) => {
    const statusLabel =
      subscription?.status === "active" ? "Activado" : "No activado";
    setPopupMessage(`Recurrencia actualizada: ${statusLabel}`);
    setIsError(false);
    setPopupOpen(true);
  }, []);
  const getTutorialProjectRowProps = useCallback((project) => {
    if (isCustomProjectsPage && project.id === CUSTOM_PROJECTS_TUTORIAL_PROJECT.id) {
      return { "data-tutorial": "tutorial-project-row" };
    }
    if (isRecurrentProjectsPage && project.id === RECURRENT_PROJECTS_TUTORIAL_FEATURED_PROJECT_ID) {
      return { "data-tutorial": "tutorial-recurrent-project-row" };
    }
    return {};
  }, [isCustomProjectsPage, isRecurrentProjectsPage]);
  const handleTutorialProjectStateChange = useCallback(() => undefined, []);
  const handleTutorialDeleteProject = useCallback(() => undefined, []);
  const handleTutorialRecurringAccess = useCallback(() => undefined, []);
  const getProjectInteractionProps = useCallback((project) => {
    const readOnly = isClientReadOnly(currentUser, project.client);
    return {
      onStateChange:
        isRecurrentProjectsPage &&
        tutorialPhase === RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU
          ? handleTutorialProjectStateChange
          : !tutorialActive && currentUser?.role !== "client" && !readOnly
          ? requestProjectStateChange
          : undefined,
      onDelete:
        isRecurrentProjectsPage &&
        tutorialPhase === RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU &&
        currentUser?.role === "agency_admin"
          ? handleTutorialDeleteProject
          : !tutorialActive && currentUser?.role === "agency_admin" && !readOnly
          ? handleDeleteProjectClick
          : undefined,
      onToggleVisibility:
        !tutorialActive &&
        currentUser?.role === "agency_admin" &&
        !readOnly &&
        project.project_type === "custom"
          ? handleToggleVisibilityClick
          : undefined,
      onConfigureRecurrence:
        projectType === "recurrent" &&
        !readOnly &&
        ["superadmin", "agency_admin", "consultant"].includes(currentUser?.role)
          ? tutorialActive
            ? handleTutorialRecurringAccess
            : handleOpenRecurringConfig
          : undefined,
      forceMenuOpen:
        isRecurrentProjectsPage &&
        tutorialPhase === RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU &&
        project.id === RECURRENT_PROJECTS_TUTORIAL_FEATURED_PROJECT_ID,
      menuButtonDataTutorial:
        isRecurrentProjectsPage &&
        project.id === RECURRENT_PROJECTS_TUTORIAL_FEATURED_PROJECT_ID
          ? "tutorial-recurrent-menu-button"
          : undefined,
      recurrenceMenuItemDataTutorial:
        isRecurrentProjectsPage &&
        project.id === RECURRENT_PROJECTS_TUTORIAL_FEATURED_PROJECT_ID
          ? "tutorial-recurrent-menu-item"
          : undefined,
      highlightRecurrenceMenuItem:
        isRecurrentProjectsPage &&
        tutorialPhase === RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU &&
        project.id === RECURRENT_PROJECTS_TUTORIAL_FEATURED_PROJECT_ID,
      canToggleVisibility:
        !tutorialActive &&
        currentUser?.role === "agency_admin" &&
        !readOnly &&
        project.project_type === "custom",
      isReadOnly: readOnly,
    };
  }, [
    currentUser,
    handleDeleteProjectClick,
    handleOpenRecurringConfig,
    requestProjectStateChange,
    handleToggleVisibilityClick,
    handleTutorialDeleteProject,
    handleTutorialProjectStateChange,
    handleTutorialRecurringAccess,
    isRecurrentProjectsPage,
    projectType,
    tutorialActive,
    tutorialPhase,
  ]);
  const handleStartRecurrentTutorialFromCompletion = useCallback(() => {
    setShowTutorialCompletionModal(false);
    navigate(
      `/projects/recurrent?${RECURRENT_PROJECTS_TUTORIAL_PARAM}=${RECURRENT_PROJECTS_TUTORIAL_VALUE}&${RECURRENT_PROJECTS_TUTORIAL_PHASE_PARAM}=${RECURRENT_PROJECTS_TUTORIAL_PHASES.OVERVIEW}`
    );
  }, [navigate]);

  // Cierra cualquier modal o vista flotante cuando el usuario navegue fuera de /projects
  useEffect(() => {
    if (!location.pathname.startsWith("/projects")) {
      setCreateModalOpen(false);
      setShouldLoadTemplates(false);
      setShouldLoadClients(false);
      setRecurringConfigModalOpen(false);
      setSelectedRecurringProject(null);
      // Añadir aquí más estados de overlays si surgieran en el futuro
    }
  }, [location.pathname]);

  useEffect(() => {
    if (currentUser?.role !== "superadmin") return;

    if (previousFilterRef.current !== selectedFilter) {
      previousFilterRef.current = selectedFilter;
      setSelectedClient("__all__");
    }
  }, [selectedFilter, currentUser?.role]);

  // Flags para controlar cuándo cargar datos para el modal de filtros
  const isAgencyScopedUser = ["agency_admin", "consultant"].includes(
    currentUser?.role
  );
  const isMultiAgencyConsultant =
    currentUser?.role === "consultant" &&
    Array.isArray(currentUser?.invited_agencies) &&
    currentUser.invited_agencies.length > 0;
  const isTemplateSelectionBlocked =
    isMultiAgencyConsultant &&
    (!createProjectClientId || createProjectClientId === "__all__");
  const shouldFetchModalAgencies =
    isFilterModalOpen && currentUser?.role === "superadmin";

  // Derivar la agencia para cargar clientes
  const derivedAgencyForClients = React.useMemo(() => {
    if (!currentUser) return null;
    if (currentUser.role === "superadmin") {
      const fallbackAgency =
        appliedAdvancedFilters.agency?.value || selectedFilter || null;
      if (isFilterModalOpen) {
        return draftAdvancedFilters.agency?.value || fallbackAgency;
      }
      return fallbackAgency;
    }
    return currentUser.agencyId || null;
  }, [
    currentUser,
    draftAdvancedFilters.agency,
    appliedAdvancedFilters.agency,
    selectedFilter,
    isFilterModalOpen,
  ]);

  const shouldFetchModalClients =
    isFilterModalOpen &&
    (currentUser?.role === "superadmin" || isAgencyScopedUser);

  const advancedAgencyId = appliedAdvancedFilters.agency?.value || null;
  const advancedClientId = appliedAdvancedFilters.client?.value || null;

  // Endpoint para clientes del filtro
  const filterClientsEndpoint = React.useMemo(() => {
    const params = new URLSearchParams();
    params.append("ordering", "name");
    if (currentUser?.role === "superadmin") {
      if (derivedAgencyForClients) {
        params.append("agency", derivedAgencyForClients);
      }
    }
    // Note: For non-superadmin, backend already filters by user's agency/access
    const queryString = params.toString();
    return `/clients/options/${queryString ? `?${queryString}` : ""}`;
  }, [currentUser?.role, derivedAgencyForClients]);

  const clientsEndpoint = useMemo(() => {
    if (!currentUser) return null;

    // Para agency_admin, solo cargar clientes cuando se abre el modal
    if (currentUser.role === "agency_admin") {
      if (!shouldLoadClients) return null;
      return `clients/options/?ordering=name`; // Backend filters by user's agency automatically
    }

    // Para consultant solo cargar clientes cuando se abre el modal
    if (currentUser.role === "consultant") {
      if (!shouldLoadClients) return null;
      return `clients/options/?ordering=name`; // Backend filters by user's assigned clients automatically
    }

    if (currentUser.role === "superadmin") {
      if (!shouldLoadClients) return null;
      if (selectedFilter) {
        return `clients/options/?agency=${selectedFilter}&ordering=name`;
      }
      return "clients/options/?ordering=name";
    }


    return null;
  }, [currentUser, selectedFilter, shouldLoadClients]);

  const { data: clientsData, isLoading: isLoadingClients } = getData(
    clientsEndpoint,
    interceptorReady && !!clientsEndpoint && currentUser?.role !== "client",
    [currentUser?.id, selectedFilter, filterRefresh, projectsRefreshKey, clientsRefreshKey]
  );

  // getData para agencias y clientes del modal de filtros
  const { data: filterAgenciesData, isLoading: isLoadingFilterAgencies } =
    getData(
      "/agencies/",
      interceptorReady && shouldFetchModalAgencies,
      [projectsRefreshKey, filterRefresh, filterModalRefreshKey]
    );

  const { data: filterClientsData, isLoading: isLoadingFilterClients } = getData(
    filterClientsEndpoint,
    interceptorReady && shouldFetchModalClients,
    [
      filterClientsEndpoint,
      draftAdvancedFilters.agency?.value,
      currentUser?.agencyId,
      filterModalRefreshKey,
    ]
  );

  const clientOptions = useMemo(() => {
    if (!clientsData) return [];

    if (currentUser?.role === "client") {
      const clientRecord =
        clientsData?.results?.[0] ||
        (Array.isArray(clientsData) ? clientsData[0] : clientsData);
      if (!clientRecord) {
        return [];
      }

      return [
        {
          value: clientRecord.id,
          label: clientRecord.name,
          agencyId:
            clientRecord.agency_id ||
            clientRecord.agency?.id ||
            clientRecord.agency ||
            null,
          agencyName: clientRecord.agency_name,
        },
      ];
    }

    const list = Array.isArray(clientsData?.results)
      ? clientsData.results
      : Array.isArray(clientsData)
        ? clientsData
        : [];

    const mappedClients = list
      .map((client) => ({
        value: client.id,
        label: client.name,
        agencyId:
          client.agency_id ||
          client.agency?.id ||
          client.agency ||
          null,
        agencyName: client.agency_name,
        contactName: client.contact_name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Para superadmin, añadir opción "Todos" al inicio
    if (currentUser?.role === "superadmin") {
      return [
        { value: "__all__", label: "Mostrar todos" },
        ...mappedClients,
      ];
    }

    return mappedClients;
  }, [clientsData, currentUser]);

  const createProjectClientOptions = useMemo(
    () => withClientAccess(clientOptions, currentUser, (option) =>
      option.value === "__all__" ? null : option.value
    ),
    [clientOptions, currentUser]
  );

  const createProjectClientInfo = useMemo(() => {
    if (!createProjectClientId || createProjectClientId === "__all__") {
      return null;
    }
    const normalizedClientId = String(createProjectClientId);
    return (
      clientOptions.find(
        (option) => String(option.value) === normalizedClientId
      ) || null
    );
  }, [clientOptions, createProjectClientId]);

  // Opciones para el modal de filtros
  const agencyOptions = React.useMemo(() => {
    if (!filterAgenciesData) return [];
    return normalizeApiList(filterAgenciesData).map((agency) => ({
      value: agency.id,
      label: agency.name,
    }));
  }, [filterAgenciesData]);

  const filterModalClientOptions = React.useMemo(() => {
    if (!filterClientsData) return [];
    const list = normalizeApiList(filterClientsData);

    const hasAgencySelected = isFilterModalOpen
      ? draftAdvancedFilters.agency?.value
      : appliedAdvancedFilters.agency?.value;

    return list.map((client) => {
      const agencyName =
        client.agency?.name ||
        client.agency_info?.name ||
        client.agency_name ||
        null;

      if (currentUser?.role === "superadmin") {
        const showAgencyInLabel = !hasAgencySelected;
        return {
          value: client.id,
          label: showAgencyInLabel && agencyName ? `${client.name} - ${agencyName}` : client.name,
          agency:
            client.agency ||
            client.agency_id ||
            client.agency?.id ||
            client.agency_info?.id ||
            null,
        };
      } else {
        return {
          value: client.id,
          label: client.name,
          agency:
            client.agency ||
            client.agency_id ||
            client.agency?.id ||
            client.agency_info?.id ||
            null,
        };
      }
    });
  }, [filterClientsData, appliedAdvancedFilters.agency, draftAdvancedFilters.agency, isFilterModalOpen, currentUser?.role]);

  const globalAgencyOption = React.useMemo(() => {
    if (
      currentUser?.role !== "superadmin" ||
      !selectedFilter ||
      !selectedFilterData?.name
    ) {
      return null;
    }
    return {
      value: selectedFilter,
      label: selectedFilterData.name,
    };
  }, [currentUser?.role, selectedFilter, selectedFilterData]);

  const globalClientOption = React.useMemo(() => {
    if (
      !isAgencyScopedUser ||
      !selectedFilter ||
      !selectedFilterData?.name
    ) {
      return null;
    }
    return {
      value: selectedFilter,
      label: selectedFilterData.name,
    };
  }, [isAgencyScopedUser, selectedFilter, selectedFilterData]);

  const isGlobalAgencyLocked =
    currentUser?.role === "superadmin" && Boolean(globalAgencyOption);
  const isGlobalClientLocked =
    isAgencyScopedUser && Boolean(globalClientOption);


  const selectedClientInfo = useMemo(
    () =>
      selectedClient === "__all__" ? null : clientOptions.find((option) => option.value === selectedClient) || null,
    [clientOptions, selectedClient]
  );

  const selectedAgencyForTemplates = useMemo(() => {
    if (!currentUser) return null;

    if (currentUser.role === "superadmin") {
      return selectedClient === "__all__" ? selectedFilter || null : selectedClientInfo?.agencyId || null;
    }

    if (currentUser.role === "agency_admin") {
      return currentUser.agencyId || null;
    }

    if (currentUser.role === "consultant") {
      if (isMultiAgencyConsultant) {
        return createProjectClientInfo?.agencyId || null;
      }
      return currentUser.agencyId || null;
    }

    if (currentUser.role === "client") {
      return clientOptions[0]?.agencyId || null;
    }

    return null;
  }, [
    currentUser,
    selectedClientInfo,
    selectedFilter,
    clientOptions,
    createProjectClientInfo,
    isMultiAgencyConsultant,
  ]);

  // Handlers para el modal de filtros
  const handleOpenFilterModal = () => {
    setDraftAdvancedFilters(cloneAdvancedFilters(appliedAdvancedFilters));
    setFilterModalRefreshKey((prev) => prev + 1);
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
    setDraftAdvancedFilters(cloneAdvancedFilters(appliedAdvancedFilters));
  };

  const handleDraftFilterChange = (field, value) => {
    setDraftAdvancedFilters((prev) => {
      const nextValue = value ? { ...value } : null;
      const updated = { ...prev, [field]: nextValue };
      if (field === "agency" && nextValue) {
        updated.client = null;
      }
      return updated;
    });
  };

  const handleClearDraftFilters = () => {
    setDraftAdvancedFilters(createEmptyAdvancedFilters());
  };

  const handleApplyAdvancedFilters = () => {
    setAppliedAdvancedFilters(cloneAdvancedFilters(draftAdvancedFilters));
    setAdvancedFiltersRefreshKey((prev) => prev + 1);
    setIsFilterModalOpen(false);
  };

  const handleRemoveAdvancedFilter = (key) => {
    setAppliedAdvancedFilters((prev) => {
      const next = cloneAdvancedFilters(prev);
      next[key] = null;
      return next;
    });
    setDraftAdvancedFilters((prev) => {
      const next = cloneAdvancedFilters(prev);
      next[key] = null;
      return next;
    });
    setAdvancedFiltersRefreshKey((prev) => prev + 1);
  };


  // Filter chips logic
  const advancedFilterChips = React.useMemo(() => {
    const chips = [];

    if (appliedAdvancedFilters.agency) {
      chips.push({
        key: "agency",
        label: appliedAdvancedFilters.agency.label,
      });
    }
    if (appliedAdvancedFilters.client) {
      chips.push({
        key: "client",
        label: appliedAdvancedFilters.client.label,
      });
    }
    return chips;
  }, [appliedAdvancedFilters]);

  const handleClearGlobalFilter = useCallback(() => {
    changeSelectedFilter("global");
  }, [changeSelectedFilter]);

  const globalFilterChip = React.useMemo(() => {
    if (!selectedFilter || !selectedFilterData?.name) {
      return null;
    }
    return {
      key: "global-filter",
      label: selectedFilterData.name,
      onRemove: handleClearGlobalFilter,
    };
  }, [selectedFilter, selectedFilterData, handleClearGlobalFilter]);

  const displayedFilterChips = React.useMemo(() => {
    const chips = [];
    if (globalFilterChip) {
      chips.push(globalFilterChip);
    }
    advancedFilterChips.forEach((chip) => {
      chips.push({
        ...chip,
        onRemove: () => handleRemoveAdvancedFilter(chip.key),
      });
    });
    return chips;
  }, [globalFilterChip, advancedFilterChips, handleRemoveAdvancedFilter]);

  // useEffect para limpiar filtros personalizados cuando cambia el selectedFilter del AppLayout
  const previousSelectedFilterRef2 = React.useRef(selectedFilter);
  useEffect(() => {
    if (previousSelectedFilterRef2.current !== selectedFilter) {
      previousSelectedFilterRef2.current = selectedFilter;

      const hasAdvancedFilters =
        appliedAdvancedFilters.agency?.value ||
        appliedAdvancedFilters.client?.value;

      if (hasAdvancedFilters) {
        setAppliedAdvancedFilters(createEmptyAdvancedFilters());
        setDraftAdvancedFilters(createEmptyAdvancedFilters());
        setAdvancedFiltersRefreshKey((prev) => prev + 1);
      }
    }
  }, [selectedFilter, appliedAdvancedFilters]);

  // Configuración de filtros para el modal genérico
  const filterConfigs = React.useMemo(() => [
    {
      key: "agency",
      title: "Agencia",
      description: "Escoge la agencia sobre la que quieres filtrar.",
      icon: Building2,
      options: agencyOptions,
      loading: isLoadingFilterAgencies,
      disabled: isGlobalAgencyLocked,
      placeholder: "Selecciona una agencia",
      loadingPlaceholder: "Cargando agencias...",
      fallbackOption: globalAgencyOption,
      showForRoles: ["superadmin"],
      role: currentUser?.role,
    },
    {
      key: "client",
      title: "Cliente",
      description: "Filtra los proyectos asociados a un cliente concreto.",
      icon: Users,
      options: filterModalClientOptions,
      loading: isLoadingFilterClients,
      disabled: isGlobalClientLocked,
      placeholder: "Selecciona un cliente",
      loadingPlaceholder: "Cargando clientes...",
      fallbackOption: globalClientOption,
      role: currentUser?.role,
    },
  ], [
    agencyOptions,
    filterModalClientOptions,
    isLoadingFilterAgencies,
    isLoadingFilterClients,
    isGlobalAgencyLocked,
    isGlobalClientLocked,
    globalAgencyOption,
    globalClientOption,
    currentUser?.role,
  ]);

  const projectsBaseQueryString = useMemo(() => {
    if (!currentUser) return null;

    const params = new URLSearchParams();
    if (currentUser.role !== "client") {
      params.set("project_type", projectTypeApiValue);
    }

    if (currentUser.role === "superadmin") {
      const agencyParam = advancedAgencyId || selectedFilter;
      if (agencyParam) {
        params.set("agency", agencyParam);
      }

      if (advancedClientId) {
        params.set("client", advancedClientId);
      } else if (selectedClient && selectedClient !== "__all__") {
        params.set("client", selectedClient);
      }
    } else if (
      currentUser.role === "agency_admin" ||
      currentUser.role === "consultant"
    ) {
      // Note: Backend already filters by user's agency, no need to add agency param
      if (advancedClientId) {
        params.set("client", advancedClientId);
      } else if (selectedFilter) {
        params.set("client", selectedFilter);
      }
    } else if (currentUser.role === "client") {
      const clientId =
        selectedFilter ||
        advancedClientId ||
        (clientIds.length === 1 ? clientIds[0] : null);
      if (clientId) {
        params.set("client", clientId);
      }
      params.set("exclude_setup", "true");
    }

    // Agregar filtro de estado si no es "Todos"
    if (!isKpiView && selectedStatus && selectedStatus !== "__all__") {
      params.set("state", selectedStatus);
    }

    // Agregar búsqueda si existe
    if (searchTerm) {
      params.set("search", searchTerm);
    }

    return params.toString();
  }, [
    currentUser,
    projectTypeApiValue,
    selectedFilter,
    selectedClient,
    selectedStatus,
    isKpiView,
    advancedAgencyId,
    advancedClientId,
    clientIds,
    searchTerm,
  ]);

  const projectsEndpoint = useMemo(() => {
    if (isKpiView) return null;
    if (!projectsBaseQueryString) return null;

    const params = new URLSearchParams(projectsBaseQueryString);
    params.set("page", projectsPage.toString());
    params.set("page_size", currentProjectsPageSize.toString());
    params.set("ordering", projectsOrdering);

    return `projects/projects/?${params.toString()}`;
  }, [currentProjectsPageSize, isKpiView, projectsBaseQueryString, projectsOrdering, projectsPage]);

  const {
    data: projectsData,
    isFetching: isFetchingProjects,
    error: projectsError,
  } = getData(projectsEndpoint, interceptorReady && !!projectsEndpoint, [
    projectsEndpoint,
    projectsRefreshKey,
    filterRefresh,
    selectedClient,
    selectedStatus,
    createProjectRefreshKey,
    advancedFiltersRefreshKey,
    localProjectsRefreshKey,
    searchRefreshKey,
    projectsOrdering,
    sortRefreshKey,
  ]);

  const recurrentKpisQueryString = useMemo(() => {
    if (!isSuperadminRecurrentKpiAvailable) return null;

    const params = new URLSearchParams();
    const agencyParam = advancedAgencyId || selectedFilter;

    if (agencyParam) {
      params.set("agency", agencyParam);
    }

    if (advancedClientId) {
      params.set("client", advancedClientId);
    } else if (selectedClient && selectedClient !== "__all__") {
      params.set("client", selectedClient);
    }

    if (searchTerm) {
      params.set("search", searchTerm);
    }

    return params.toString();
  }, [
    advancedAgencyId,
    advancedClientId,
    isSuperadminRecurrentKpiAvailable,
    searchTerm,
    selectedClient,
    selectedFilter,
  ]);

  const recurrentKpisEndpoint = useMemo(() => {
    if (!isKpiView || !isSuperadminRecurrentKpiAvailable) return null;
    return `projects/projects/recurrent-kpis/${
      recurrentKpisQueryString ? `?${recurrentKpisQueryString}` : ""
    }`;
  }, [isKpiView, isSuperadminRecurrentKpiAvailable, recurrentKpisQueryString]);

  const {
    data: recurrentKpisData,
    isFetching: isFetchingRecurrentKpis,
    error: recurrentKpisError,
  } = getData(recurrentKpisEndpoint, interceptorReady && !!recurrentKpisEndpoint, [
    recurrentKpisEndpoint,
    projectsRefreshKey,
    filterRefresh,
    selectedFilter,
    selectedClient,
    advancedFiltersRefreshKey,
    searchRefreshKey,
    createProjectRefreshKey,
  ]);

  const recurrentKpiAgencyDetailQueryString = useMemo(() => {
    if (!selectedKpiAgency?.agency_id || !isSuperadminRecurrentKpiAvailable) {
      return null;
    }

    const params = new URLSearchParams();

    if (advancedClientId) {
      params.set("client", advancedClientId);
    } else if (selectedClient && selectedClient !== "__all__") {
      params.set("client", selectedClient);
    }

    if (searchTerm) {
      params.set("search", searchTerm);
    }

    return params.toString();
  }, [
    advancedClientId,
    isSuperadminRecurrentKpiAvailable,
    searchTerm,
    selectedClient,
    selectedKpiAgency?.agency_id,
  ]);

  const recurrentKpiAgencyDetailEndpoint = useMemo(() => {
    if (!selectedKpiAgency?.agency_id || !isSuperadminRecurrentKpiAvailable) {
      return null;
    }

    return `projects/projects/recurrent-kpis/agency/${selectedKpiAgency.agency_id}/${
      recurrentKpiAgencyDetailQueryString ? `?${recurrentKpiAgencyDetailQueryString}` : ""
    }`;
  }, [
    isSuperadminRecurrentKpiAvailable,
    recurrentKpiAgencyDetailQueryString,
    selectedKpiAgency?.agency_id,
  ]);

  const {
    data: recurrentKpiAgencyDetailData,
    isFetching: isFetchingRecurrentKpiAgencyDetail,
    error: recurrentKpiAgencyDetailError,
  } = getData(
    recurrentKpiAgencyDetailEndpoint,
    interceptorReady && !!recurrentKpiAgencyDetailEndpoint,
    [
      recurrentKpiAgencyDetailEndpoint,
      projectsRefreshKey,
      filterRefresh,
      selectedClient,
      advancedFiltersRefreshKey,
      searchRefreshKey,
      createProjectRefreshKey,
    ]
  );

  const templatesEndpoint = useMemo(() => {
    if (!currentUser) return null;

    // Para superadmin, usar endpoint directo sin parámetros
    if (currentUser.role === "superadmin") {
      return "projects/templates/";
    }

    const params = new URLSearchParams();
    params.set("template_type", projectTypeApiValue);

    if (isMultiAgencyConsultant) {
      if (!selectedAgencyForTemplates) return null;
      params.set("agency", selectedAgencyForTemplates);
    }

    return `projects/templates/?${params.toString()}`;
  }, [
    currentUser,
    selectedAgencyForTemplates,
    isMultiAgencyConsultant,
    projectTypeApiValue,
  ]);

  const { data: templatesData, isLoading: isLoadingTemplates } = getData(
    templatesEndpoint,
    interceptorReady && !!templatesEndpoint && shouldLoadTemplates,
    [templatesEndpoint, selectedClient, filterRefresh, templatesRefreshKey, projectsRefreshKey]
  );

  const templateOptions = useMemo(() => {
    if (isTemplateSelectionBlocked) return [];
    if (!templatesData) return [];

    const list = Array.isArray(templatesData?.results)
      ? templatesData.results
      : Array.isArray(templatesData)
        ? templatesData
        : [];

    // Filtrar plantillas de tipo "setup"
    return list
      .filter((template) => template.template_type.toLowerCase() !== "setup")
      .map((template) => ({
        value: template.id,
        label: template.name,
        description: template.description,
      }));
  }, [templatesData, isTemplateSelectionBlocked]);

  const projects = useMemo(() => {
    if (!projectsList.length) return [];

    return projectsList.map((project) => ({
      ...project,
      progress_percentage: project.progress_percentage ?? 0,
      // Aplicar overrides locales
      ...(localProjectsOverrides[project.id] || {})
    }));
  }, [projectsList, localProjectsOverrides]);

  // Update status stats from backend response
  useEffect(() => {
    const sourceStateStats = isKpiView
      ? recurrentKpisData?.state_stats
      : projectsData?.state_stats;

    if (sourceStateStats) {
      setStatusStats({
        in_progress: sourceStateStats.in_progress || 0,
        not_started: sourceStateStats.not_started || 0,
        completed: sourceStateStats.completed || 0,
        all: sourceStateStats.all || 0,
      });
    }
  }, [isKpiView, projectsData, recurrentKpisData]);

  useEffect(() => {
    if (isKpiView) return;
    if (!projectsData) return;

    if (Array.isArray(projectsData)) {
      setProjectsList(projectsData);
      setHasMoreProjects(false);
      setTotalProjects(projectsData.length);
      return;
    }

    const results = Array.isArray(projectsData?.results)
      ? projectsData.results
      : [];
    const totalCount =
      typeof projectsData?.count === "number" ? projectsData.count : null;

    setProjectsList((prev) => {
      if (isTableView || projectsPage === 1) {
        return results;
      }

      const seen = new Set(prev.map((project) => project.id));
      const merged = results.filter((project) => !seen.has(project.id));
      return [...prev, ...merged];
    });
    if (totalCount !== null) {
      const totalPages = Math.ceil(totalCount / currentProjectsPageSize);
      setHasMoreProjects(projectsPage < totalPages);
      setTotalProjects(totalCount);
    } else {
      setHasMoreProjects(Boolean(projectsData?.next));
      setTotalProjects(null);
    }
  }, [currentProjectsPageSize, isKpiView, isTableView, projectsData, projectsPage]);

  useEffect(() => {
    setProjectsPage(1);
    setProjectsList([]);
    setHasMoreProjects(true);
    setTotalProjects(null);
  }, [
    selectedClient,
    selectedStatus,
    searchTerm,
    projectsOrdering,
    selectedFilter,
    advancedAgencyId,
    advancedClientId,
    projectsRefreshKey,
    filterRefresh,
    createProjectRefreshKey,
    advancedFiltersRefreshKey,
    localProjectsRefreshKey,
    tableRowsPerPage,
  ]);

  useEffect(() => {
    setProjectsPage(1);
  }, [effectiveViewMode]);

  useEffect(() => {
    if (!isKpiView && isKpiAgencyDetailOpen) {
      setIsKpiAgencyDetailOpen(false);
    }
  }, [isKpiView, isKpiAgencyDetailOpen]);

  useEffect(() => {
    if (!["cards", "kanban"].includes(effectiveViewMode)) return;
    if (!loadMoreRef.current || !hasMoreProjects) return;
    if (projectsList.length === 0) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetchingProjects) {
          setProjectsPage((prev) => {
            if (!hasMoreProjects) return prev;
            if (typeof totalProjects === "number" && totalProjects > 0) {
              const totalPages = Math.ceil(totalProjects / PROJECTS_PAGE_SIZE);
              if (prev >= totalPages) return prev;
            }
            return prev + 1;
          });
        }
      },
      { rootMargin: "200px 0px" }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [effectiveViewMode, hasMoreProjects, isFetchingProjects, projectsList.length, totalProjects]);

  const handleTablePageChange = useCallback((newPage) => {
    setProjectsPage(newPage + 1);
  }, []);

  const handleTableRowsPerPageChange = useCallback((nextRowsPerPage) => {
    setTableRowsPerPage(nextRowsPerPage);
    setProjectsPage(1);
  }, []);

  const handleSortFieldChange = useCallback((nextSortField) => {
    if (nextSortField === sortField) return;
    setProjectsPage(1);
    setProjectsList([]);
    setHasMoreProjects(true);
    setTotalProjects(null);
    setSortField(nextSortField);
    setSortRefreshKey((prev) => prev + 1);
  }, [sortField]);

  const handleSortDirectionChange = useCallback((nextSortDirection) => {
    if (nextSortDirection === sortDirection) return;
    setProjectsPage(1);
    setProjectsList([]);
    setHasMoreProjects(true);
    setTotalProjects(null);
    setSortDirection(nextSortDirection);
    setSortRefreshKey((prev) => prev + 1);
  }, [sortDirection]);

  const filteredProjects = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return projects.filter((project) => {
      const projectName = (project.name || project.template_name || "").toLowerCase();
      const clientName = (project.client_name || "").toLowerCase();
      const matchesSearch =
        !normalizedSearch ||
        projectName.includes(normalizedSearch) ||
        clientName.includes(normalizedSearch);

      return matchesSearch;
    });
  }, [projects, searchTerm]);

  // Cuando el tutorial está activo sustituimos los proyectos reales por mock data
  const displayedProjects = tutorialActive ? tutorialMockProjects : filteredProjects;

  const projectsCount = isKpiView
    ? recurrentKpisData?.summary?.total_recurrent_projects || 0
    : projects.length;

  const defaultClientIdForModal = useMemo(() => {
    if (!currentUser) return null;

    if (currentUser.role === "superadmin") {
      return selectedClient === "__all__" ? null : selectedClient;
    }

    if (
      currentUser.role === "agency_admin" ||
      currentUser.role === "consultant"
    ) {
      return selectedFilter && selectedFilter !== "global"
        ? selectedFilter
        : null;
    }

    if (currentUser.role === "client") {
      return currentUser.client || null;
    }

    return null;
  }, [currentUser, selectedClient, selectedFilter]);

  const canCreateProject = useMemo(() => {
    if (!currentUser) return false;

    if (currentUser.role === "superadmin") {
      return Boolean(selectedClient && selectedClient !== "__all__" && selectedAgencyForTemplates);
    }

    if (
      currentUser.role === "agency_admin" ||
      currentUser.role === "consultant"
    ) {
      // Para agency_admin, permitir abrir el modal incluso sin clientes cargados aún
      // La validación de clientes se hará dentro del modal
      if (currentUser.role === "consultant" && isMultiAgencyConsultant) {
        return true;
      }
      return Boolean(selectedAgencyForTemplates);
    }

    if (currentUser.role === "client") {
      return Boolean(currentUser.client && selectedAgencyForTemplates);
    }

    return false;
  }, [currentUser, selectedClient, selectedAgencyForTemplates]);

  const handleViewProgress = useCallback((project) => {
    navigate(`/projects/${projectTypeConfig.routeSegment}/${project.id}`);
  }, [navigate, projectTypeConfig.routeSegment]);

  const handleOpenKpiAgencyDetail = useCallback((agency) => {
    setSelectedKpiAgency(agency);
    setIsKpiAgencyDetailOpen(true);
  }, []);

  const handleCloseKpiAgencyDetail = useCallback(() => {
    setIsKpiAgencyDetailOpen(false);
  }, []);

  const handleKpiAgencyDetailExited = useCallback(() => {
    setSelectedKpiAgency(null);
  }, []);

  const progressActionIcon = useMemo(() => <ArrowRight size={14} />, []);

  const handleCreateProject = async (payload) => {
    if (isClientReadOnly(currentUser, payload.client)) {
      setPopupMessage("Este cliente está en modo solo lectura y no admite nuevos proyectos.");
      setIsError(true);
      setPopupOpen(true);
      return;
    }
    setIsSubmitting(true);
    try {
      const requestBody = {
        client: payload.client,
        start_date: payload.startDate,
      };

      if (payload.template) {
        requestBody.template = payload.template;
      }

      if (payload.name) {
        requestBody.name = payload.name;
      }

      if (payload.description) {
        requestBody.description = payload.description;
      }

      if (payload.is_internal !== undefined) {
        requestBody.is_internal = payload.is_internal;
      }

      if (!payload.template && Array.isArray(payload.phases)) {
        requestBody.phases = payload.phases.map((phase, phaseIndex) => ({
          name: phase.name,
          description: phase.description || "",
          icon: phase.icon || "tabler-target",
          initial_state: phase.initial_state || "not_started",
          order: phase.order || phaseIndex + 1,
          only_tasks: Boolean(phase.only_tasks),
          tasks: Array.isArray(phase.tasks)
            ? phase.tasks.map((task, taskIndex) => ({
              name: task.name,
              description: task.description || "",
              initial_state: task.initial_state || "planeado",
              documentation_urls: task.documentation_urls || [],
              days_to_deadline:
                task.days_to_deadline === "" || task.days_to_deadline === null
                  ? null
                  : Number(task.days_to_deadline),
              order: task.order || taskIndex + 1,
            }))
            : [],
        }));
      }

      await apiClient.post("projects/projects/", requestBody);
      refreshWelcomeProgress();

      // Resetear el estado de carga de clientes cuando se cierra el modal
      if (currentUser?.role === "agency_admin") {
        setShouldLoadClients(false);
      }
      setCreateProjectRefreshKey((prev) => prev + 1);
      setPopupMessage("Proyecto creado correctamente.");
      setIsError(false);
      setPopupOpen(true);
    } catch (error) {
      setPopupMessage(extractErrorMessage(error));
      setIsError(true);
      setPopupOpen(true);
    } finally {
      setIsSubmitting(false);
      setCreateModalOpen(false); // Cerrar modal siempre
      setCreateProjectClientId(null);
      setShouldLoadTemplates(false);
    }
  };

  useEffect(() => {
    if (!createModalOpen || !isMultiAgencyConsultant) return;
    setShouldLoadTemplates(
      Boolean(createProjectClientId && createProjectClientId !== "__all__")
    );
  }, [createModalOpen, isMultiAgencyConsultant, createProjectClientId]);

  useEffect(() => {
    if (!currentUser) return;

    const projectSingularLabel = projectTypeConfig.singularLabel;
    const projectPluralLabel = projectTypeConfig.pluralLabel;

    const titleByRole = (() => {
      switch (currentUser.role) {
        case "superadmin":
          return `Gestión de proyectos ${projectPluralLabel}`;
        case "agency_admin":
          return `Proyectos ${projectPluralLabel} de la agencia`;
        case "consultant":
          return `Proyectos ${projectPluralLabel} asignados`;
        case "client":
          if (projectsCount === 1) {
            return `Proyecto ${projectSingularLabel}`;
          }
          return `Proyectos ${projectPluralLabel}`;
        default:
          return `Proyectos ${projectPluralLabel}`;
      }
    })();

    const subtitleByRole = (() => {
      switch (currentUser.role) {
        case "client":
          if (projectsCount === 1) {
            return `Seguimiento del proyecto ${projectSingularLabel} activo con Markezy`;
          }
          return `Seguimiento de los proyectos ${projectPluralLabel} activos con Markezy`;
        case "consultant":
          return `Visualiza y actualiza el progreso de proyectos ${projectPluralLabel} de tus clientes`;
        default:
          return `Crea, administra y consulta el estado de los proyectos ${projectPluralLabel}`;
      }
    })();

    const rightContent = (
      <Stack direction="row" spacing={1.5} alignItems="center">
        {currentUser.role !== "client" && (
          <>
            {/* En recurrent, Plantillas solo está disponible para superadmin */}
            {(projectType !== "recurrent" || currentUser.role === "superadmin") && (
              <Button
                data-tutorial="projects-templates-button"
                variant="dark"
                onClick={() =>
                  navigate(
                    `/projects/templates?from=${projectTypeConfig.routeSegment}`
                  )
                }
                size="md"
              >
                <Layers size={16} />
                Plantillas
              </Button>
            )}

            {/* Botón de Nuevo proyecto - solo para agency_admin y consultant */}
            {currentUser.role !== "superadmin" && projectType !== "recurrent" && (
              <Button
                data-tutorial="projects-new-button"
                size="md"
                onClick={() => {
                  setCreateProjectClientId(defaultClientIdForModal || null);
                  setShouldLoadTemplates(!isMultiAgencyConsultant || Boolean(defaultClientIdForModal));
                  setShouldLoadClients(true);
                  setTemplatesRefreshKey((prev) => prev + 1);
                  if (currentUser?.role === "agency_admin" || currentUser?.role === "consultant") {
                    setClientsRefreshKey((prev) => prev + 1);
                  }
                  setCreateModalOpen(true);
                }}
                disabled={
                  !canCreateProject ||
                  isSubmitting ||
                  isLoadingTemplates ||
                  (isLoadingClients && shouldLoadClients)
                }
              >
                <Plus size={16} />
                Nuevo proyecto
              </Button>
            )}
          </>
        )}
      </Stack>
    );

    setPageContext({
      title: titleByRole,
      subtitle: subtitleByRole,
      showTitle: true,
      titleRightContent: canStartProjectsTutorial ? (
        <TutorialStartButton
          onClick={() => {
            setIsFirstTimeTutorialPrompt(false);
            setShowTutorialIntroModal(true);
          }}
        />
      ) : null,
      rightContent,
      breadcrumbItems: currentUser?.role === "client" ? null : selectedFilterData
        ? selectedFilterData.name === "Vista Global"
          ? [{ label: selectedFilterData.name }]
          : [
            {
              label: "Vista Global",
              onClick: () => changeSelectedFilter("global"),
            },
            { label: selectedFilterData.name },
          ]
        : undefined,
    });
  }, [
    currentUser,
    canCreateProject,
    canStartProjectsTutorial,
    changeSelectedFilter,
    clientOptions,
    isLoadingClients,
    isLoadingTemplates,
    isSubmitting,
    navigate,
    selectedClient,
    selectedFilterData,
    projectsCount,
    projectTypeConfig.pluralLabel,
    projectTypeConfig.singularLabel,
    setPageContext,
  ]);

  const emptyProjectsMessage = React.useMemo(() => {
    if (currentUser?.role === "client" && selectedFilter) {
      const clientName = selectedFilterData?.name || "la empresa seleccionada";
      return `No tienes proyectos ${projectTypeConfig.pluralLabel} asociados a la empresa: ${clientName}`;
    }

    if (projectType === "custom") {
      return `Sin proyectos ${projectTypeConfig.pluralLabel}. Crea uno a partir de una plantilla para comenzar a trabajar.`;
    }

    return "Aún no se han generado proyectos recurrentes";
  }, [
    currentUser?.role,
    projectTypeConfig.pluralLabel,
    selectedFilter,
    selectedFilterData?.name,
    projectType,
  ]);

  const pageError = isKpiView ? recurrentKpisError : projectsError;

  if (pageError) {
    return (
      <EmptyStateContainer
        title="No pudimos cargar los proyectos"
        message="Revisa tu conexión o vuelve a intentarlo en unos minutos."
        badgeText="Error"
      />
    );
  }

  const isLoadingInitial = isKpiView
    ? isFetchingRecurrentKpis && !recurrentKpisData
    : isFetchingProjects && projects.length === 0;
  const isLoadingMore = isFetchingProjects && projectsPage > 1;
  // Sin respuesta aún (query no lanzada o en curso) no se muestra ni vacío ni kanban
  // En tutorial la lista es mock: se muestra el vacío sin esperar al backend.
  const isProjectsEmpty = tutorialActive
    ? displayedProjects.length === 0
    : projectsData !== undefined && displayedProjects.length === 0 && !isFetchingProjects;

  // Toolbar común: va integrado en la tabla o como card propia en las vistas KPI/tarjetas.
  const projectsToolbarProps = {
    searchValue: searchInput,
    onSearchChange: setSearchInput,
    searchPlaceholder: isKpiView ? "Buscar clientes o agencias..." : "Buscar proyectos...",
    tabs: [
      { value: "__all__", label: "Todos", count: statusStats.all },
      { value: "in_progress", label: "En progreso", count: statusStats.in_progress },
      { value: "not_started", label: "No iniciados", count: statusStats.not_started },
      { value: "completed", label: "Completados", count: statusStats.completed },
    ],
    activeTab: isKpiView ? null : selectedStatus,
    tabsDisabled: isKpiView,
    onTabChange: (value) => {
      if (value === selectedStatus) return;
      setSelectedStatus(value);
      // Forzar refresco y limpiar overrides al cambiar de tab
      setLocalProjectsRefreshKey((prev) => prev + 1);
      setLocalProjectsOverrides({});
    },
    filterChips: displayedFilterChips,
    // Visible también en tutorial (lo explica un paso); allí la vista queda fijada en tabla.
    toolbarEnd: (
      <ViewModeSelector
        options={viewModeOptions}
        value={effectiveViewMode}
        onChange={tutorialActive ? () => {} : setViewMode}
        tooltipTitle="Escoger vista"
        buttonSx={toolbarViewModeButtonSx}
      />
    ),
    ...(!tutorialActive &&
      !isKpiView && {
        sortFieldOptions: SORT_FIELD_OPTIONS,
        sortField,
        sortDirection,
        sortDirectionOptions: SORT_DIRECTION_OPTIONS,
        onSortFieldChange: handleSortFieldChange,
        onSortDirectionChange: handleSortDirectionChange,
      }),
    // Botón de filtros avanzados - solo para no clientes
    onFilterClick: currentUser?.role !== "client" ? handleOpenFilterModal : undefined,
    filterTooltip: "Editar filtros",
  };
  return (
    <Box sx={{ pb: { xs: "80px", md: 0 } }}>
      {isKpiView || effectiveViewMode !== "table" ? (
        <Box data-tutorial="projects-toolbar" sx={{ mb: 3 }}>
          <DataTableToolbar variant="standalone" {...projectsToolbarProps} />
        </Box>
      ) : null}

      {isKpiView ? (
        <RecurrentProjectsKpiView
          data={recurrentKpisData}
          isLoading={isFetchingRecurrentKpis && !recurrentKpisData}
          onViewDetails={handleOpenKpiAgencyDetail}
        />
      ) : effectiveViewMode === "table" ? (
        tutorialActive ? (
          <ProjectsTableView
            rows={displayedProjects}
            totalCount={displayedProjects.length}
            isFetching={false}
            error={null}
            currentPage={0}
            rowsPerPage={tableRowsPerPage}
            onPageChange={noopTutorialHandler}
            onRowsPerPageChange={noopTutorialHandler}
            isRecurrentProjectsTable={projectType === "recurrent"}
            showClientColumn={shouldShowClientColumn}
            emptyMessage={emptyProjectsMessage}
            onViewProgress={noopTutorialHandler}
            getProjectInteractionProps={getProjectInteractionProps}
            toolbarProps={projectsToolbarProps}
            getRowProps={getTutorialProjectRowProps}
          />
        ) : (
          <ProjectsTableView
            rows={projects}
            totalCount={typeof totalProjects === "number" ? totalProjects : projects.length}
            isFetching={isFetchingProjects}
            error={projectsError}
            currentPage={Math.max(projectsPage - 1, 0)}
            rowsPerPage={tableRowsPerPage}
            onPageChange={handleTablePageChange}
            onRowsPerPageChange={handleTableRowsPerPageChange}
            isRecurrentProjectsTable={projectType === "recurrent"}
            showClientColumn={shouldShowClientColumn}
            emptyMessage={emptyProjectsMessage}
            onViewProgress={handleViewProgress}
            getProjectInteractionProps={getProjectInteractionProps}
            toolbarProps={projectsToolbarProps}
          />
        )
      ) : effectiveViewMode === "kanban" ? (
        <>
          {isProjectsEmpty ? (
            <EmptyStateMessage
              icon={projectType === "recurrent" ? Repeat : FolderOpen}
              message={emptyProjectsMessage}
              sx={{ py: 6, color: "text.secondary" }}
            />
          ) : displayedProjects.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <ProjectsKanbanView
              projects={displayedProjects}
              columnCounts={selectedStatus === "__all__" && !searchTerm.trim() ? statusStats : null}
              isLoading={false}
              isLoadingMore={isLoadingMore && hasMoreProjects}
              onViewProgress={handleViewProgress}
              getProjectInteractionProps={getProjectInteractionProps}
            />
          )}
          {hasMoreProjects && <Box ref={loadMoreRef} sx={{ height: 1 }} />}
        </>
      ) : (
        <>
          {isLoadingInitial && !tutorialActive ? (
            <ProjectsGridSkeleton />
          ) : isProjectsEmpty ? (
            <Grid container spacing={3} data-tutorial="projects-list">
              <Grid size={{ xs: 12 }}>
                <EmptyStateMessage
                  icon={projectType === "recurrent" ? Repeat : FolderOpen}
                  message={emptyProjectsMessage}
                  sx={{ py: 6, color: "text.secondary" }}
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3} data-tutorial="projects-list">
              {displayedProjects.map((project) => {
                const interactionProps = getProjectInteractionProps(project);

                return (
                  <Grid size={{ xs: 12, md: 6, xl: 4 }} key={project.id}>
                    <ProjectCard
                      project={project}
                      onClick={tutorialActive ? undefined : handleViewProgress}
                      {...interactionProps}
                      readOnly={interactionProps.isReadOnly}
                      title={project.name || project.template_name}
                      infoItems={[
                        { icon: Users, text: project.client_name },
                        {
                          icon: Layers,
                          text: project.template_name || "Proyecto sin plantilla",
                        },
                      ]}
                      secondaryInfoItems={[
                        { icon: Calendar, text: `Inicio: ${formatDate(project.start_date)}` },
                        ...(project.is_internal && project.project_type === "custom"
                          ? [{ icon: EyeOff, text: "Ocultado al cliente" }]
                          : []),
                      ]}
                      showActionButton={!tutorialActive}
                      actionButtonLabel="Progreso"
                      actionButtonIcon={progressActionIcon}
                      onActionClick={handleViewProgress}
                    />
                  </Grid>
                );
              })}
              {isLoadingMore &&
                hasMoreProjects &&
                Array.from({ length: 3 }).map((_, index) => (
                  <Grid
                    size={{ xs: 12, md: 6, xl: 4 }}
                    key={`project-skeleton-more-${index}`}
                  >
                    <Card>
                      <CardContent sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box>
                            <Skeleton variant="text" width="65%" height={28} />
                            <Skeleton variant="text" width="45%" height={18} />
                          </Box>
                          <Skeleton variant="rounded" height={8} />
                          <Skeleton variant="text" width="55%" height={18} />
                          <Skeleton variant="rounded" height={36} />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
            </Grid>
          )}
          {effectiveViewMode === "cards" && hasMoreProjects && (
            <Box ref={loadMoreRef} sx={{ height: 1 }} />
          )}
        </>
      )}

      <IncidentFiltersModal
        open={isFilterModalOpen}
        onClose={handleCloseFilterModal}
        title="Filtros avanzados"
        filterConfigs={filterConfigs}
        draftFilters={draftAdvancedFilters}
        onChange={handleDraftFilterChange}
        onApply={handleApplyAdvancedFilters}
        onClearAll={handleClearDraftFilters}
      />

      <CreateProjectModal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setCreateProjectClientId(null);
          setShouldLoadTemplates(false);
          // Resetear el estado de carga de clientes cuando se cierra el modal
          if (currentUser?.role === "agency_admin") {
            setShouldLoadClients(false);
          }
        }}
        clients={createProjectClientOptions}
        templates={templateOptions}
        defaultClientId={defaultClientIdForModal}
        onClientChange={(value) => {
          const nextClientId = value || null;
          setCreateProjectClientId(nextClientId);
          if (isMultiAgencyConsultant) {
            setShouldLoadTemplates(Boolean(nextClientId));
          }
        }}
        templatesDisabled={isTemplateSelectionBlocked}
        templatesHelperText={
          isTemplateSelectionBlocked
            ? "Selecciona un cliente para seleccionar la plantilla."
            : null
        }
        onSubmit={handleCreateProject}
        isSubmitting={isSubmitting}
      />

      <RecurringProjectConfigModal
        open={recurringConfigModalOpen}
        onClose={handleCloseRecurringConfig}
        clientId={selectedRecurringProject?.client}
        clientName={selectedRecurringProject?.client_name}
        refreshKey={recurringConfigRefreshKey}
        appRefreshKey={projectsRefreshKey}
        onSaved={handleRecurringConfigSaved}
      />

      <RecurrentProjectsAgencyDetailModal
        open={isKpiAgencyDetailOpen}
        onClose={handleCloseKpiAgencyDetail}
        onExited={handleKpiAgencyDetailExited}
        agency={selectedKpiAgency}
        data={recurrentKpiAgencyDetailData}
        isLoading={isFetchingRecurrentKpiAgencyDetail && !recurrentKpiAgencyDetailData}
        error={recurrentKpiAgencyDetailError}
      />

      {/* Modal de confirmación para eliminar proyecto */}
      <ConfirmModal
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDeleteProjectId(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Eliminar proyecto"
        description="¿Estás seguro de que deseas eliminar este proyecto? Esta acción eliminará todas las fases y tareas asociadas."
        confirmLabel="Eliminar"
        tone="danger"
      />

      {/* Modal de confirmación para ocultar/mostrar proyecto */}
      <ConfirmModal
        open={visibilityConfirmOpen}
        onClose={() => {
          setVisibilityConfirmOpen(false);
          setVisibilityProjectId(null);
          setVisibilityCurrentState(false);
        }}
        onConfirm={handleConfirmToggleVisibility}
        title={visibilityCurrentState ? "Mostrar proyecto al cliente" : "Ocultar proyecto al cliente"}
        description={
          visibilityCurrentState ? (
            "¿Estás seguro de que deseas hacer visible este proyecto al cliente? Se creará un nuevo thread de comunicación para este proyecto."
          ) : (
            <>
              ¿Estás seguro de que deseas ocultar este proyecto al cliente? <strong>El thread de comunicación desaparecerá, así como todos sus mensajes.</strong> Esta acción no se puede deshacer.
            </>
          )
        }
        confirmLabel={visibilityCurrentState ? "Mostrar" : "Ocultar"}
        tone={visibilityCurrentState ? "info" : "warning"}
      />

      <ConfirmModal
        open={stateConfirmOpen}
        onClose={() => {
          setStateConfirmOpen(false);
          setPendingStateChange(null);
        }}
        onConfirm={handleConfirmProjectStateChange}
        title="Marcar proyecto como completado"
        description="¿Seguro que quieres marcar este proyecto como completado?"
        confirmLabel="Confirmar"
      />

      <SuccessPopup
        isOpen={popupOpen}
        message={popupMessage}
        error={isError}
        onClose={() => setPopupOpen(false)}
      />

      {/* ── TUTORIAL: modal de crear proyecto falso ───────────────────────── */}
      <CreateProjectModal
        open={tutorialCreateModalOpen}
        onClose={resetTutorial}
        clients={TUTORIAL_CLIENT_OPTIONS}
        templates={TUTORIAL_TEMPLATE_OPTIONS}
        defaultClientId={CUSTOM_PROJECTS_TUTORIAL_CLIENT.value}
        tutorialInitialState={CUSTOM_PROJECTS_TUTORIAL_PROJECT_FORM}
        onSubmit={() => {}}
        isSubmitting={false}
        onClientChange={() => {}}
        templatesDisabled={false}
        templatesHelperText={null}
        disableBackdropClose
        dialogProps={TUTORIAL_DIALOG_PROPS}
      />

      <RecurringProjectConfigModal
        open={tutorialRecurringConfigModalOpen}
        onClose={resetTutorial}
        clientId={RECURRENT_PROJECTS_TUTORIAL_CLIENT.id}
        clientName={RECURRENT_PROJECTS_TUTORIAL_CLIENT.name}
        tutorialData={RECURRENT_PROJECTS_TUTORIAL_SUBSCRIPTION}
        dialogProps={TUTORIAL_DIALOG_PROPS}
      />

      <UnifiedModal
        open={tutorialClientPreviewOpen}
        onClose={resetTutorial}
        title="Acceso desde clientes"
        maxWidth="md"
        {...TUTORIAL_DIALOG_PROPS}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ width: "100%" }}>
            <CompactClientCard
              client={RECURRENT_PROJECTS_TUTORIAL_CLIENT}
              setSelectedClient={() => undefined}
              onClientDeleted={() => undefined}
              onClientUpdated={() => undefined}
              availableConsultants={[]}
              onSuccess={() => undefined}
              onError={() => undefined}
              onViewOnboarding={() => undefined}
              packLabels={RECURRENT_PROJECTS_TUTORIAL_PACK_LABELS}
              onRecurringConfigClick={handleTutorialRecurringAccess}
              recurringButtonDataTutorial="tutorial-client-recurring-button"
              highlightRecurringButton
              recurringButtonTooltip="Acceso directo a la recurrencia desde la ficha de cliente"
            />
          </Box>
        </Box>
      </UnifiedModal>

      {/* ── TUTORIAL: Joyride multi-fase ──────────────────────────────────── */}
      <TutorialTargetProxies steps={currentTutorialJoyrideSteps} active={tutorialActive} />
      {/* El scroll hasta cada target lo hace runWhenTutorialTargetReady/prepareTutorialStep. */}
      <Joyride
        floaterProps={TUTORIAL_FLOATER_PROPS}
        key={`${projectType}-projects-tutorial-${tutorialJoyrideKey}`}
        steps={currentTutorialJoyrideSteps}
        run={tutorialsEnabled && isTutorialJoyrideRunning}
        stepIndex={tutorialStepIndex}
        continuous
        showSkipButton={false}
        showProgress={false}
        disableOverlayClose
        disableCloseOnEsc
        disableScrolling
        disableScrollParentFix
        locale={{ close: "Cerrar", last: "Siguiente", next: "Siguiente" }}
        tooltipComponent={TutorialJoyrideTooltip}
        styles={getTutorialJoyrideStyles(theme, currentTutorialJoyrideZIndex)}
        callback={handleTutorialJoyrideCallback}
      />

      {/* ── TUTORIAL: modal de intro ──────────────────────────────────────── */}
      <TutorialIntroModal
        open={tutorialsEnabled && showTutorialIntroModal}
        onClose={handleCloseTutorialIntro}
        onConfirm={startProjectsTutorial}
        isFirstTimePrompt={isFirstTimeTutorialPrompt}
        {...currentTutorialIntro}
      />

      {/* ── TUTORIAL: modal de finalización ──────────────────────────────── */}
      <TutorialCompletionModal
        open={tutorialsEnabled && showTutorialCompletionModal}
        onClose={() => setShowTutorialCompletionModal(false)}
        description={currentTutorialCompletionDescription}
        note={
          canOfferRecurrentTutorial
            ? "Si quieres seguir, el tutorial de proyectos recurrentes te enseña cómo se generan cada mes y dónde se configura su recurrencia."
            : undefined
        }
        secondaryButtonLabel={canOfferRecurrentTutorial ? "Omitir" : null}
        buttonLabel={canOfferRecurrentTutorial ? "Ver tutorial" : "Cerrar"}
        onSecondaryAction={
          canOfferRecurrentTutorial
            ? () => setShowTutorialCompletionModal(false)
            : undefined
        }
        onPrimaryAction={
          canOfferRecurrentTutorial
            ? handleStartRecurrentTutorialFromCompletion
            : () => setShowTutorialCompletionModal(false)
        }
      />
      {/* ── FIN TUTORIAL ──────────────────────────────────────────────────── */}
    </Box >
  );
}
